/**
 * M2 (Ely 10-31): where a listing sells. The marketplace id -> country table, the regions and countryOfMarketplace are
 * api-contracts' (core/catalog/markets.ts, EN-4574), shared with the API's per-country facet count; this file adds the
 * picker's helpers. '' (Shopify and other stores) and unknown ids have no country.
 */
import {
  MARKET_REGIONS,
  MARKETPLACE_COUNTRIES,
} from '@skupreme/api-contracts/core/catalog/markets';

export {
  countryOfMarketplace,
  MARKET_REGIONS,
} from '@skupreme/api-contracts/core/catalog/markets';

/** The filter token of listings with no country (marketplace id ''): stores such as Shopify. */
export const NO_COUNTRY = 'none';

/**
 * The countries a selection reaches (Catalog Division Scope 6): picking a country keeps the no-country stores, since
 * they have no country to exclude them by. Only picking nothing but NO_COUNTRY narrows to them; empty stays empty.
 */
export const withNoCountry = (countries: readonly string[]): string[] =>
  countries.length && !countries.includes(NO_COUNTRY)
    ? [...countries, NO_COUNTRY]
    : [...countries];

/** The region a country is in ("North America"), or null for a country the table does not list. */
export const regionOfCountry = (country: string): string | null =>
  MARKET_REGIONS.find(r => r.countries.includes(country))?.name ?? null;

const COUNTRY_NAMES = new Intl.DisplayNames(['en'], { type: 'region' });

/** "United States", "Mexico"; the code itself when the runtime has no name. */
export const countryName = (country: string): string =>
  COUNTRY_NAMES.of(country) ?? country;

/** The country's flag as regional-indicator emoji ("🇺🇸"); no image assets. */
export const countryFlag = (country: string): string =>
  /^[A-Z]{2}$/.test(country)
    ? String.fromCodePoint(
        ...[...country].map(c => 0x1f1e6 + c.charCodeAt(0) - 65),
      )
    : '';

/**
 * Every marketplace id that sells in these countries, for the list's `markets` filter: the Amazon and eBay ids and the
 * code itself (channels that send the country). NO_COUNTRY stays as the token for marketplace id ''.
 */
export const marketplaceIdsOf = (countries: readonly string[]): string[] => {
  const wanted = new Set(countries);
  const ids: string[] = [];
  for (const [id, country] of Object.entries(MARKETPLACE_COUNTRIES)) {
    if (wanted.has(country)) ids.push(id);
  }
  for (const country of countries) ids.push(country);
  return ids;
};
